/**
 * Calculator formula for recalculating buy list calculator item prices.
 * Extracted from Calculator page to enable price sync.
 */

// All currency codes that need price calculations
const CURRENCY_CODES = [
    'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'AED', 'RUB', 'CNY', 'KRW', 'INR', 'ZAR', 'BRL', 'MXN',
    'MYR', 'SEK', 'DKK', 'NOK', 'CHF', 'PLN', 'CZK', 'HRK', 'TRY', 'RON', 'SAR', 'KWD', 'JOD',
    'EGP', 'MAD', 'DZD', 'TND', 'ETB', 'UZS',
];

/**
 * Recalculate prices for a calculator buy list item using current market prices.
 * This formula matches Calculator page calculation exactly.
 * 
 * @param {Object} item - Calculator item with pt, pd, rh, weightCeramic, moisture, shippingCost
 * @param {Object} dailyPrices - DailyPrices document with market prices and currency rates
 * @param {Object} charges - Charges document with deductions and refining costs
 * @returns {Object} Object with priceUSD, priceEUR, priceGBP, etc.
 */
export function recalculateCalcItemPrices(item, dailyPrices, charges) {
    const ozGrams = 31.1034768;
    const grams = Number(item.weightCeramic) || 0;
    const moisture = Number(item.moisture) || 0;
    const effWeight = grams * ((100 - moisture) / 100);

    // Metal deductions
    const plat = (Number(item.pt) || 0) * (Number(charges.metalDeductionPlat) || 0);
    const pall = (Number(item.pd) || 0) * (Number(charges.metalDeductionPall) || 0);
    const rhod = (Number(item.rh) || 0) * (Number(charges.metalDeductionRhod) || 0);

    // Market prices with ounce deductions
    let marketPlat = (Number(dailyPrices.platUSD) || 0) - (Number(charges.ounceDeductionPlat) || 0);
    let marketPall = (Number(dailyPrices.pallUSD) || 0) - (Number(charges.ounceDeductionPall) || 0);
    let marketRhod = (Number(dailyPrices.rhodUSD) || 0) - (Number(charges.ounceDeductionRhod) || 0);

    // Treatment charge uses raw weight (not moisture-adjusted)
    const priceReduction = grams * ((Number(charges.treatmentCharge) || 0) / 1000);

    // Refining costs
    const refiningCostPlat = (plat / 1000) * (Number(charges.refiningCostPlat) || 0);
    const refiningCostPall = (pall / 1000) * (Number(charges.refiningCostPall) || 0);
    const refiningCostRhod = (rhod / 1000) * (Number(charges.refiningCostRhod) || 0);

    // Convert to per-gram from per-oz
    marketPlat = marketPlat / ozGrams;
    marketPall = marketPall / ozGrams;
    marketRhod = marketRhod / ozGrams;

    // Metal prices
    const pricePlat = (plat * marketPlat * effWeight) / 1_000_000;
    const pricePall = (pall * marketPall * effWeight) / 1_000_000;
    const priceRhod = (rhod * marketRhod * effWeight) / 1_000_000;

    let totalPrice =
        pricePlat + pricePall + priceRhod - refiningCostPlat - refiningCostPall - refiningCostRhod - priceReduction;

    // Leasing costs
    const leasingPlat =
        ((pricePlat * (Number(charges.leasingPlat) || 0)) / 360) * (Number(charges.leasingPlatDays) || 0);
    const leasingPall =
        ((pricePall * (Number(charges.leasingPall) || 0)) / 360) * (Number(charges.leasingPallDays) || 0);
    const leasingRhod =
        ((priceRhod * (Number(charges.leasingRhod) || 0)) / 360) * (Number(charges.leasingRhodDays) || 0);
    totalPrice = totalPrice - leasingPlat - leasingPall - leasingRhod;

    // Shipping (expenses) per kg
    const shippingCost = Number(item.shippingCost) || 0;
    const shipping = shippingCost * (effWeight / 1000);
    totalPrice = totalPrice - shipping;

    if (totalPrice < 0) totalPrice = 0;
    totalPrice = Math.round(totalPrice * 100) / 100;

    // Generate all currency prices
    const prices = {
        priceUSD: totalPrice,
    };

    for (const code of CURRENCY_CODES) {
        if (code === 'USD') continue;
        const rateKey = code.toLowerCase() + 'USD';
        const rate = Number(dailyPrices[rateKey]) || 1;
        prices[`price${code}`] = Math.round(totalPrice * rate * 100) / 100;
    }

    return prices;
}
